/**
 * SideEffectsEditor - structured editor for the action's
 * `side_effects` list.
 *
 * Fed by `/admin/effect-handlers/`: the "+ Add side effect" dropdown
 * lists every registered handler; picking one inserts an entry with
 * the handler's default phase + failure policy, and renders a
 * <HandlerConfigForm /> built from its `config_schema`. Rows can be
 * reordered and removed inline.
 */

import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  useEffectHandlerCatalog,
  type EffectHandlerCatalogEntry,
  type WorkflowSideEffectEntry,
} from '@/admin/hooks/use-workflow-definitions'

import { HandlerConfigForm } from './handler-config-form'

const PHASE_OPTIONS = [
  { value: 'pre_commit', label: 'pre_commit - before save' },
  { value: 'post_commit', label: 'post_commit - after save' },
]

const FAILURE_POLICY_OPTIONS = [
  { value: 'fail', label: 'fail - rollback on error' },
  { value: 'warn', label: 'warn - log + continue' },
  { value: 'ignore', label: 'ignore - silent' },
]

interface SideEffectsEditorProps {
  value: WorkflowSideEffectEntry[]
  onChange: (next: WorkflowSideEffectEntry[]) => void
}

export function SideEffectsEditor({ value, onChange }: SideEffectsEditorProps) {
  const { data: catalog = [], isLoading } = useEffectHandlerCatalog()
  const byKey = new Map(catalog.map((h) => [h.key, h]))

  function updateAt(idx: number, patch: Partial<WorkflowSideEffectEntry>) {
    onChange(
      value.map((entry, i) => (i === idx ? { ...entry, ...patch } : entry)),
    )
  }
  function removeAt(idx: number) {
    onChange(value.filter((_, i) => i !== idx))
  }
  function addHandler(key: string) {
    const handler = byKey.get(key)
    if (!handler) return
    onChange([
      ...value,
      {
        handler: handler.key,
        config: {},
        phase: handler.default_phase ?? 'post_commit',
        failure_policy: handler.default_failure_policy ?? 'fail',
        idempotent: true,
        when: {},
      },
    ])
  }
  function move(idx: number, dir: -1 | 1) {
    const target = idx + dir
    if (target < 0 || target >= value.length) return
    const next = [...value]
    ;[next[idx], next[target]] = [next[target], next[idx]]
    onChange(next)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[10px] text-[var(--ink-3)]">
          Side effects run in the listed order once the action's transition
          commits. Use <code className="font-mono">pre_commit</code> for guards
          that can abort the transaction.
        </p>
        <AddHandlerDropdown
          handlers={catalog}
          loading={isLoading}
          onAdd={addHandler}
        />
      </div>

      {value.length === 0 ? (
        <div className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
          No side effects. The action will commit with no downstream work.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {value.map((entry, idx) => (
            <SideEffectRow
              key={idx}
              index={idx}
              total={value.length}
              entry={entry}
              handler={byKey.get(entry.handler)}
              onPatch={(patch) => updateAt(idx, patch)}
              onRemove={() => removeAt(idx)}
              onMove={(dir) => move(idx, dir)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function AddHandlerDropdown({
  handlers,
  loading,
  onAdd,
}: {
  handlers: EffectHandlerCatalogEntry[]
  loading: boolean
  onAdd: (key: string) => void
}) {
  return (
    <Select
      value=""
      onValueChange={(next: string) => {
        if (next) onAdd(next)
      }}
    >
      <SelectTrigger className="h-7 w-[220px] text-[11px]">
        <span className="flex items-center gap-1">
          <Plus size={11} />
          <SelectValue placeholder="Add side effect…" />
        </span>
      </SelectTrigger>
      <SelectContent>
        {loading && (
          <div className="px-3 py-2 text-[11px] text-[var(--ink-3)]">
            Loading…
          </div>
        )}
        {!loading && handlers.length === 0 && (
          <div className="px-3 py-2 text-[11px] text-[var(--ink-3)]">
            No handlers registered.
          </div>
        )}
        {handlers.map((h) => (
          <SelectItem key={h.key} value={h.key}>
            <div className="flex flex-col">
              <span className="font-mono text-[11px]">{h.key}</span>
              {h.label && h.label !== h.key && (
                <span className="text-[10px] text-[var(--ink-3)]">
                  {h.label}
                </span>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

interface SideEffectRowProps {
  index: number
  total: number
  entry: WorkflowSideEffectEntry
  handler: EffectHandlerCatalogEntry | undefined
  onPatch: (patch: Partial<WorkflowSideEffectEntry>) => void
  onRemove: () => void
  onMove: (dir: -1 | 1) => void
}

function SideEffectRow({
  index,
  total,
  entry,
  handler,
  onPatch,
  onRemove,
  onMove,
}: SideEffectRowProps) {
  return (
    <div className="flex flex-col gap-3 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <span className="font-mono text-[12px] text-[var(--ink)]">
            {entry.handler}
          </span>
          {handler?.description && (
            <span className="text-[10px] text-[var(--ink-3)]">
              {handler.description}
            </span>
          )}
          {!handler && (
            <span className="text-[10px] text-[var(--status-error)]">
              Handler not found in registry. It may have been renamed.
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={() => onMove(-1)}
            disabled={index === 0}
            title="Move up"
          >
            <ArrowUp size={13} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={() => onMove(1)}
            disabled={index === total - 1}
            title="Move down"
          >
            <ArrowDown size={13} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={onRemove}
            title="Remove side effect"
          >
            <Trash2 size={13} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
            Phase
          </Label>
          <Select
            value={entry.phase ?? 'post_commit'}
            onValueChange={(next: string) => onPatch({ phase: next })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PHASE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
            Failure policy
          </Label>
          <Select
            value={entry.failure_policy ?? 'fail'}
            onValueChange={(next: string) => onPatch({ failure_policy: next })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FAILURE_POLICY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2 flex items-center gap-2">
          <input
            type="checkbox"
            checked={entry.idempotent ?? false}
            onChange={(e) => onPatch({ idempotent: e.target.checked })}
            className="h-3.5 w-3.5"
          />
          <Label className="text-[11px] text-[var(--ink-2)]">
            Idempotent - safe to retry with the same inputs.
          </Label>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Config
        </Label>
        <HandlerConfigForm
          schema={handler?.config_schema}
          value={(entry.config ?? {}) as Record<string, unknown>}
          onChange={(next) => onPatch({ config: next })}
        />
      </div>
    </div>
  )
}
